import { useState, useEffect, useCallback } from 'react';
import { controlHHApi, trabajadoresApi, rrhhApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { SearchableSelect } from '../../components/SearchableSelect';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import {
  Clock, Users, Briefcase, TrendingUp, ChevronRight,
  Trash2, ArrowLeft, Loader2, Filter, X
} from 'lucide-react';

const KPI = ({ label, value, sub, icon: Icon, color }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`p-2 rounded-xl ${color}`}><Icon className="h-4 w-4" strokeWidth={1.5} /></div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-lg font-bold tabular-nums">{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

export default function ControlHHUnificado() {
  const [view, setView] = useState('general');
  const [selectedMes, setSelectedMes] = useState('');
  const [selectedWorker, setSelectedWorker] = useState(null);

  // Filters
  const [filterOT, setFilterOT] = useState(null);
  const [filterWorker, setFilterWorker] = useState(null);
  const [otsActivas, setOtsActivas] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);

  // Data
  const [general, setGeneral] = useState(null);
  const [mensual, setMensual] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const otOptions = otsActivas.map(ot => ({
    value: ot.numero_ot,
    label: `OT ${ot.numero_ot} - ${(ot.descripcion || '').substring(0, 40)}`,
    prefix: `${ot.numero_ot}`,
    secondary: `${ot.numero_ot} ${ot.descripcion || ''} ${ot.cliente_nombre || ''}`,
  }));

  const workerOptions = allWorkers.map(w => ({
    value: w.numero,
    label: w.nombre,
    prefix: `${w.numero}`,
    secondary: `${w.nombre} ${w.cargo || ''} ${w.numero}`,
  }));

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [resOT, resW] = await Promise.all([
          rrhhApi.getOTsActivas(),
          trabajadoresApi.getAll({ activo: true }),
        ]);
        setOtsActivas(resOT.data);
        setAllWorkers(resW.data);
      } catch (err) { console.warn('[ControlHHUnificado] No se pudo cargar OTs/trabajadores:', err); }
    };
    loadRefs();
    // Reset explícito de filtros al entrar a la vista (defensa contra cualquier
    // caso donde React Router mantuviera el componente montado o haya caché).
    setFilterOT(null);
    setFilterWorker(null);
    setSelectedMes('');
    setSelectedWorker(null);
  }, []);  

  const loadGeneral = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterOT) params.ot_numero = filterOT;
      if (filterWorker) params.trabajador_numero = filterWorker;
      const res = await controlHHApi.resumenGeneral(params);
      setGeneral(res.data);
      setView('general');
      setMensual(null);
      setDetalle(null);
    } catch {
      toast({ title: 'Error al cargar resumen', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filterOT, filterWorker]);

  useEffect(() => { loadGeneral(); }, [loadGeneral]);

  const loadMensual = async (mes) => {
    setSelectedMes(mes);
    setView('mensual');
    try {
      setLoading(true);
      const params = { mes };
      if (filterOT) params.ot_numero = filterOT;
      const res = await controlHHApi.resumenMensual(params);
      setMensual(res.data);
    } catch {
      toast({ title: 'Error al cargar mes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadDetalle = async (trabajador_numero, nombre) => {
    setSelectedWorker({ numero: trabajador_numero, nombre });
    setView('detalle');
    try {
      setLoading(true);
      const res = await controlHHApi.detallePersona({ trabajador_numero, mes: selectedMes });
      setDetalle(res.data);
    } catch {
      toast({ title: 'Error al cargar detalle', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este registro?')) return;
    setDeleting(id);
    try {
      await controlHHApi.delete(id);
      toast({ title: 'Registro eliminado' });
      loadDetalle(selectedWorker.numero, selectedWorker.nombre);
    } catch {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const goBack = () => {
    if (view === 'detalle') { setView('mensual'); setDetalle(null); }
    else if (view === 'mensual') { setView('general'); setMensual(null); }
  };

  const clearFilters = () => { setFilterOT(null); setFilterWorker(null); };
  const hasFilters = filterOT || filterWorker;

  if (loading && !general && !mensual && !detalle) return <TableSkeleton rows={8} cols={6} />;

  const g = general || { totals: {}, meses: [], filter: {} };
  const filterLabel = g.filter?.ot_descripcion ? `OT ${g.filter.ot_numero} - ${g.filter.ot_descripcion}` : g.filter?.trabajador_nombre ? `${g.filter.trabajador_nombre}` : '';

  // ========================= BREADCRUMB =========================
  const Breadcrumb = () => (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
      <button onClick={() => setView('general')} className={`hover:text-foreground transition-colors ${view === 'general' ? 'text-foreground font-medium' : ''}`}>
        {hasFilters ? `Filtro: ${filterLabel || 'Activo'}` : 'Ultimos 12 meses'}
      </button>
      {(view === 'mensual' || view === 'detalle') && (
        <>
          <ChevronRight className="h-3 w-3" />
          <button onClick={() => { setView('mensual'); setDetalle(null); }} className={`hover:text-foreground transition-colors ${view === 'mensual' ? 'text-foreground font-medium' : ''}`}>{selectedMes}</button>
        </>
      )}
      {view === 'detalle' && selectedWorker && (
        <>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">{selectedWorker.nombre}</span>
        </>
      )}
    </div>
  );

  // ========================= FILTERS =========================
  const FilterBar = () => (
    <div className="flex flex-wrap items-center gap-3">
      <Filter className="h-4 w-4 text-muted-foreground" />
      <div className="w-64">
        <SearchableSelect
          value={filterOT}
          onChange={(v) => setFilterOT(v)}
          options={otOptions}
          placeholder="Filtrar por OT..."
          data-testid="filter-ot"
        />
      </div>
      <div className="w-64">
        <SearchableSelect
          value={filterWorker}
          onChange={(v) => setFilterWorker(v)}
          options={workerOptions}
          placeholder="Filtrar por trabajador..."
          data-testid="filter-worker"
        />
      </div>
      {hasFilters && (
        <Button variant="ghost" size="sm" onClick={clearFilters} className="rounded-xl gap-1 text-xs" data-testid="btn-clear-filters">
          <X className="h-3 w-3" /> Limpiar
        </Button>
      )}
    </div>
  );

  // ========================= GENERAL =========================
  if (view === 'general') {
    return (
      <div className="space-y-4 page-enter" data-testid="control-hh-general">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Control HH</h1>
          <Breadcrumb />
        </div>

        <FilterBar />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Total HH" value={g.totals.total_horas || 0} sub={`${g.totals.total_hn || 0} HN + ${g.totals.total_hex || 0} HEX`} icon={Clock} color="bg-primary/10 text-primary" />
          <KPI label="Registros" value={g.totals.registros || 0} icon={TrendingUp} color="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" />
          <KPI label="Meses" value={g.meses.length} icon={Briefcase} color="bg-blue-500/10 text-blue-600 dark:text-blue-400" />
          <KPI label="Promedio/Mes" value={g.meses.length ? Math.round((g.totals.total_horas || 0) / g.meses.length) : 0} sub="HH promedio" icon={Users} color="bg-amber-500/10 text-amber-600 dark:text-amber-400" />
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mes</TableHead>
                  <TableHead className="text-right">HN</TableHead>
                  <TableHead className="text-right">HEX</TableHead>
                  <TableHead className="text-right">Total HH</TableHead>
                  <TableHead className="text-right">Trabajadores</TableHead>
                  <TableHead className="text-right">OTs</TableHead>
                  <TableHead className="text-right">Registros</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {g.meses.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{hasFilters ? 'Sin datos para este filtro' : 'Sin datos de HH'}</TableCell></TableRow>
                ) : g.meses.map((m) => (
                  <TableRow key={m.mes} className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => loadMensual(m.mes)} data-testid={`mes-row-${m.mes}`}>
                    <TableCell className="font-medium">{m.mes}</TableCell>
                    <TableCell className="text-right tabular-nums">{m.total_hn}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{m.total_hex > 0 ? m.total_hex : '-'}</TableCell>
                    <TableCell className="text-right tabular-nums font-bold">{m.total_horas}</TableCell>
                    <TableCell className="text-right tabular-nums">{m.cant_trabajadores}</TableCell>
                    <TableCell className="text-right tabular-nums">{m.cant_ots}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{m.registros}</TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))}
                {g.meses.length > 0 && (
                  <TableRow className="bg-muted/30 font-bold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-right tabular-nums">{g.totals.total_hn}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{g.totals.total_hex}</TableCell>
                    <TableCell className="text-right tabular-nums">{g.totals.total_horas}</TableCell>
                    <TableCell colSpan={4} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========================= MENSUAL =========================
  if (view === 'mensual') {
    const md = mensual || { mes: selectedMes, resumen: [] };
    const semanas = md.semanas || [];
    const totals = (md.resumen || []).reduce((a, r) => ({
      hn: a.hn + (r.total_hn || 0), hex: a.hex + (r.total_hex || 0),
      total: a.total + (r.total_horas || 0), dias: a.dias + (r.dias_trabajados || 0),
    }), { hn: 0, hex: 0, total: 0, dias: 0 });

    return (
      <div className="space-y-4 page-enter" data-testid="control-hh-mensual">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={goBack} className="rounded-xl h-8 w-8"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Control HH — {selectedMes}</h1>
            <Breadcrumb />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Total HH" value={totals.total.toFixed(1)} icon={Clock} color="bg-primary/10 text-primary" />
          <KPI label="HN" value={totals.hn.toFixed(1)} icon={Briefcase} color="bg-blue-500/10 text-blue-600 dark:text-blue-400" />
          <KPI label="HEX" value={totals.hex.toFixed(1)} icon={TrendingUp} color="bg-amber-500/10 text-amber-600 dark:text-amber-400" />
          <KPI label="Trabajadores" value={(md.resumen || []).length} icon={Users} color="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" />
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N</TableHead>
                  <TableHead>Trabajador</TableHead>
                  <TableHead className="text-right">HN</TableHead>
                  <TableHead className="text-right">HEX</TableHead>
                  {semanas.map(s => (
                    <TableHead key={s.key} className="text-right text-[10px] whitespace-nowrap text-amber-700 dark:text-amber-400">HE {s.label}</TableHead>
                  ))}
                  <TableHead className="text-right">Total HH</TableHead>
                  <TableHead className="text-right">Dias</TableHead>
                  <TableHead className="text-right">OTs</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8 + semanas.length} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                ) : (md.resumen || []).length === 0 ? (
                  <TableRow><TableCell colSpan={8 + semanas.length} className="text-center py-8 text-muted-foreground">Sin datos para {selectedMes}</TableCell></TableRow>
                ) : (
                  <>
                    {(md.resumen || []).map((r) => (
                      <TableRow key={r.trabajador_numero} className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => loadDetalle(r.trabajador_numero, r.nombre)} data-testid={`worker-row-${r.trabajador_numero}`}>
                        <TableCell className="font-mono text-sm tabular-nums">{r.trabajador_numero}</TableCell>
                        <TableCell className="font-medium">{r.nombre || '-'}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.total_hn}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.total_hex > 0 ? <span className="text-amber-600 dark:text-amber-400">{r.total_hex}</span> : '-'}</TableCell>
                        {semanas.map(s => {
                          const v = r.hex_semanas?.[s.key] || 0;
                          return (
                            <TableCell key={s.key} className={`text-right tabular-nums text-xs ${v > 12 ? 'text-red-600 font-bold' : v > 8 ? 'text-amber-600 font-semibold' : 'text-muted-foreground'}`} data-testid={`hex-sem-${r.trabajador_numero}-${s.key}`}>
                              {v > 0 ? v : '-'}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-right tabular-nums font-bold">{r.total_horas}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.dias_trabajados}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.cant_ots}</TableCell>
                        <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-bold">
                      <TableCell colSpan={2}>TOTALES</TableCell>
                      <TableCell className="text-right tabular-nums">{totals.hn.toFixed(1)}</TableCell>
                      <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{totals.hex.toFixed(1)}</TableCell>
                      {semanas.map(s => {
                        const tot = (md.resumen || []).reduce((a, r) => a + (r.hex_semanas?.[s.key] || 0), 0);
                        return <TableCell key={s.key} className="text-right tabular-nums text-xs text-amber-600 dark:text-amber-400">{tot > 0 ? tot.toFixed(1) : '-'}</TableCell>;
                      })}
                      <TableCell className="text-right tabular-nums">{totals.total.toFixed(1)}</TableCell>
                      <TableCell className="text-right tabular-nums">{totals.dias}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        {semanas.length > 0 && (
          <p className="text-[11px] text-muted-foreground" data-testid="leyenda-he-semanal">
            HE acumuladas por semana (Lun–Dom): <span className="text-amber-600 font-semibold">ámbar &gt;8h</span> · <span className="text-red-600 font-bold">rojo &gt;12h</span> (referencia límite legal: 2h extra/día)
          </p>
        )}
      </div>
    );
  }

  // ========================= DETALLE =========================
  if (view === 'detalle') {
    const dd = detalle || { registros: [], nombre: '', total_hn: 0, total_hex: 0, total_horas: 0 };

    return (
      <div className="space-y-4 page-enter" data-testid="control-hh-detalle">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={goBack} className="rounded-xl h-8 w-8"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{dd.nombre || `Trabajador #${selectedWorker?.numero}`}</h1>
            <Breadcrumb />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <KPI label="Total HH" value={dd.total_horas} icon={Clock} color="bg-primary/10 text-primary" />
          <KPI label="HN" value={dd.total_hn} icon={Briefcase} color="bg-blue-500/10 text-blue-600 dark:text-blue-400" />
          <KPI label="HEX" value={dd.total_hex} icon={TrendingUp} color="bg-amber-500/10 text-amber-600 dark:text-amber-400" />
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>OT</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead className="text-right">HN</TableHead>
                  <TableHead className="text-right">HEX</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Obs.</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                ) : (dd.registros || []).length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Sin registros</TableCell></TableRow>
                ) : (
                  <>
                    {(dd.registros || []).map((r) => (
                      <TableRow key={r.id} className="hover:bg-muted/50 transition-colors" data-testid={`record-${r.id}`}>
                        <TableCell className="text-sm tabular-nums">{r.fecha}</TableCell>
                        <TableCell>
                          {r.ot_numero === 0 ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">Gasto Gral.</span>
                          ) : (
                            <span className="text-sm font-medium">OT {r.ot_numero}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{r.area_origen || '-'}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.horas_normales}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.horas_extra > 0 ? <span className="text-amber-600 dark:text-amber-400">{r.horas_extra}</span> : '-'}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold">{r.total_horas}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate">{r.observaciones || '-'}</TableCell>
                        <TableCell>
                          {(r.source === 'web' || r.source === 'parte_diario') && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} disabled={deleting === r.id} className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive" data-testid={`btn-del-${r.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-bold">
                      <TableCell colSpan={3}>TOTAL ({(dd.registros || []).length} registros)</TableCell>
                      <TableCell className="text-right tabular-nums">{dd.total_hn}</TableCell>
                      <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{dd.total_hex}</TableCell>
                      <TableCell className="text-right tabular-nums">{dd.total_horas}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
